import React, { useEffect, useState } from 'react';
import CircularProgress from "@material-ui/core/CircularProgress";
import ExpansionPanel from "@material-ui/core/ExpansionPanel";
import Button from '@material-ui/core/Button';
import ExpansionPanelSummary from "@material-ui/core/ExpansionPanelSummary";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import InfoIcon from '@material-ui/icons/Info';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';

import { loadApplicationsDetails, purchaseApplication } from "../../api/applications";
import { ApplicationDetails } from "../ApplicationDetails/ApplicationDetails";
import { TermsAndConditions } from "../TermsAndConditions/TermsAndConditions";
import { PaymentForm } from "./PaymentForm";

import cn from "./styles.module.scss";

export const ApplyOnlinePayment = ({ appNonce }) => {
  const [applications, setApplications] = useState([]);
  const [printApplication, setPrintApplication] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentInProgress, setPaymentInProgress] = useState(false);
  const [paymentFormTouched, setPaymentFormTouched] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const paymentCompleted = applications.length > 0 && !applications.find(application => application.status !== 'active');

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const apps = await loadApplicationsDetails(appNonce);
        setApplications(apps);
      } catch (e) {
        console.log(e);
      }
      setIsLoading(false);
    })();
  }, [appNonce]);

  const handleSubmit = async (form) => {
    window.scrollTo(0, 0);
    setPaymentInProgress(true);
    try {
      for (let i = 0; i < applications.length; i++) {
        const application = applications[i];
        if (application.status !== 'active') {
          await purchaseApplication(application.app_nonce, form);
          setApplications(
            (applications) => applications.map(item => {
              if (item === application) {
                return {...item, status: 'active' }
              }
              return item;
            })
          );
        }
      }
    } catch (e) {
      setPaymentError(e.error || 'Oops. Something went wrong');
      console.error(e);
    }

    setPaymentFormTouched(true);
    setPaymentInProgress(false);
  };

  const handleErrorClose = () => {
    setPaymentError('');
  };

  const handlePrintClick = (application) => {
    setPrintApplication(application);
    setTimeout(() => window.print(), 300);
  };

  return (
    <div className={cn.content}>
      {isLoading && (
        <div className={cn.loader}>
          <CircularProgress />
        </div>
      )}
      <div>
        {applications.map((application) => {
          const isPaid = application.status === 'active';
          return (
            <div className={cn.expandablePanelWrapper} key={application.app_nonce}>
              <ExpansionPanel
                classes={{ root: cn.expandablePanel }}
              >
                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                  {application.member_firstname} {application.member_lastname}
                </ExpansionPanelSummary>
                <ExpansionPanelDetails>
                  <div className={cn.panelDetails}>
                    <ApplicationDetails application={application} />
                    {isPaid && (
                      <Button
                        className={cn.printButton}
                        variant="contained"
                        color="primary"
                        onClick={() => handlePrintClick(application)}
                      >
                        Print application
                      </Button>)
                    }
                  </div>
                </ExpansionPanelDetails>
              </ExpansionPanel>
              {isPaid ? (
                <CheckCircleIcon htmlColor="green" className={cn.icon} />
              ) : (
                paymentInProgress ? <CircularProgress className={cn.loadIcon} /> :
                  paymentFormTouched && <InfoIcon htmlColor="red" className={cn.icon} />
              )}
            </div>
          )
        })}
      </div>

      {applications.length > 0 && !paymentCompleted && (
        <PaymentForm onSubmit={handleSubmit} paymentInProgress={paymentInProgress} />
      )}

      {paymentCompleted && (
        <div className={cn.result}>
          <Alert severity="success">
            <AlertTitle>Thank you</AlertTitle>
            Your Application(s) Have(s) Been Generated Successfully.
          </Alert>
          <Button
            className={cn.returnButton}
            variant="contained"
            color="primary"
            href="/apasi-members"
          >
            Return to members screen
          </Button>
        </div>
      )}

      <div className={cn.printArea}>
        {printApplication && (
          <>
            <ApplicationDetails application={printApplication} />
            <div className={cn.pageBreak}/>
            <div className={cn.terms}>
              <TermsAndConditions />
            </div>
          </>
        )}
      </div>

      <Snackbar open={!!paymentError} onClose={handleErrorClose}>
        <Alert variant="filled" severity="error" onClose={handleErrorClose}>
          {paymentError}
        </Alert>
      </Snackbar>
    </div>
  );
};
